import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Siren, Quote, ShieldCheck } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

export default function FlareLogPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/flare-logs")
      .then((r) => { if (!cancelled) setLogs(r.data || []); })
      .catch(() => { if (!cancelled) setLogs([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Layout>
      <button
        onClick={() => navigate("/settings")}
        className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4 hover:text-white transition-colors"
        data-testid="back-to-settings"
      >
        <ArrowLeft size={16} /> Settings
      </button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="bps-eyebrow text-[#F87171]">Pain SOS</div>
        <h1 className="font-display text-3xl font-light tracking-tighter mt-1">Flare reflections</h1>
        <p className="text-sm text-[#9AAEC6] mt-2 leading-relaxed">
          Every time you tapped the SOS button — the moment, the trigger, the pattern.
          The more you log, the clearer your nervous system's signal becomes.
        </p>

        <div className="mt-5 bps-card p-4 flex items-center gap-4" data-testid="flare-summary">
          <div className="w-12 h-12 rounded-full bg-[#F87171]/15 flex items-center justify-center shrink-0">
            <Siren size={20} className="text-[#F87171]" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">
              {logs === null ? "…" : logs.length} {logs?.length === 1 ? "flare" : "flares"} logged
            </div>
            <div className="text-xs text-[#9AAEC6]">
              {logs === null
                ? "Loading…"
                : logs.length === 0
                  ? "No flares yet. The red SOS button at the top of any screen will start one."
                  : "Look for repeated triggers — that's where the rewiring happens."}
            </div>
          </div>
        </div>

        {logs && logs.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="flare-list">
            {logs.map((f) => (
              <div key={f.id} className="bps-card p-5" data-testid={`flare-entry-${f.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-[#F87171]">
                    <Siren size={11} /> Flare
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8]">
                    {new Date(f.created_at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Quote size={13} className="text-[#22D3EE] mt-1 shrink-0" />
                  <div className="text-sm text-white/90 leading-relaxed whitespace-pre-wrap flex-1">
                    {f.trigger_text}
                  </div>
                </div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#22D3EE]">
                  <ShieldCheck size={11} /> Transformer protocol initiated
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </Layout>
  );
}
